(() => {
  'use strict';
  if (customElements.get('ges-price-filter')) return;
  const nativeKeys = ['filter.v.price.gte', 'filter.v.price.lte'];
  const marketKeys = ['ges_price_min', 'ges_price_max'];
  const contextKeys = ['ges_price_currency', 'ges_price_country'];
  const nativeContext = ['ges_price_native_currency', 'ges_price_native_country'];
  const allKeys = [...nativeKeys, ...marketKeys, ...contextKeys, ...nativeContext];
  const spanish = () => (document.documentElement.lang || '').startsWith('es');
  const words = (es, en) => spanish() ? es : en;
  const amount = value => value === null || value === '' ? null : Number(value);
  class GESPriceFilter extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.form = this.querySelector('form');
      this.min = this.querySelector('[data-min]'); this.max = this.querySelector('[data-max]');
      this.status = this.querySelector('[data-status]');
      this.results = this.closest('results-list');
      if (!this.results) return;
      this.panel = this.querySelector('[popover]');
      this.uiController = new AbortController();
      const uiOptions = { signal: this.uiController.signal };
      this.results.addEventListener('click', event => {
        const trigger = event.target.closest('[data-ges-price-toggle]');
        if (trigger) this.trigger = trigger;
      }, uiOptions);
      this.panel?.addEventListener('beforetoggle', event => {
        if (event.newState === 'open') requestAnimationFrame(() => this.positionPanel());
      }, uiOptions);
      this.panel?.addEventListener('toggle', event => {
        this.results.querySelectorAll('[data-ges-price-toggle]').forEach(button => button.setAttribute('aria-expanded', String(event.newState === 'open')));
      }, uiOptions);
      window.addEventListener('resize', () => this.positionPanel(), uiOptions);
      window.addEventListener('scroll', () => this.positionPanel(), { ...uiOptions, passive: true, capture: true });
      window.visualViewport?.addEventListener('resize', () => this.positionPanel(), uiOptions);
      this.form.addEventListener('submit', event => { event.preventDefault(); this.apply(); }, uiOptions);
      this.querySelector('[data-clear]').addEventListener('click', () => {
        const url = new URL(location.href); allKeys.forEach(key => url.searchParams.delete(key)); url.searchParams.delete('page'); location.assign(url);
      }, uiOptions);
      const url = new URL(location.href), params = url.searchParams;
      // A range belongs to the exact current market; never reuse another currency's numbers.
      if (params.has(contextKeys[0]) && (params.get(contextKeys[0]) !== this.dataset.currency || params.get(contextKeys[1]) !== this.dataset.country)) {
        allKeys.forEach(key => params.delete(key)); params.delete('page'); location.replace(url); return;
      }
      if (params.has(nativeContext[0]) && (params.get(nativeContext[0]) !== this.dataset.currency || params.get(nativeContext[1]) !== this.dataset.country)) {
        allKeys.forEach(key => params.delete(key)); params.delete('page'); location.replace(url); return;
      }
      // Recover old native links in a market where Shopify does not expose price facets.
      if (this.dataset.native !== 'true' && nativeKeys.some(key => params.has(key))) {
        nativeKeys.forEach((key, i) => { if (params.has(key)) params.set(marketKeys[i], params.get(key)); params.delete(key); });
        params.set(contextKeys[0], this.dataset.currency); params.set(contextKeys[1], this.dataset.country);
        params.delete('page'); location.replace(url); return;
      }
      this.syncInputs();
      try {
        const step = new Intl.NumberFormat('en', { style: 'currency', currency: this.dataset.currency }).resolvedOptions().maximumFractionDigits === 0 ? '1' : '.01';
        this.min.step = step; this.max.step = step;
      } catch {}
      if (params.has(contextKeys[0]) && marketKeys.some(key => params.has(key))) this.scan();
    }
    disconnectedCallback() { this.controller?.abort(); this.uiController?.abort(); this.bound = false; }
    positionPanel() {
      if (!this.panel?.matches(':popover-open')) return;
      const trigger = this.trigger?.isConnected && this.trigger.getBoundingClientRect().width ? this.trigger : [...this.results.querySelectorAll('[data-ges-price-toggle]')].find(button => button.getBoundingClientRect().width);
      if (!trigger) { this.panel.hidePopover(); return; }
      const viewport = window.visualViewport;
      this.panel.style.maxHeight = `${Math.max(120, (viewport?.height || innerHeight) - 32)}px`;
      const rect = trigger.getBoundingClientRect(), panel = this.panel.getBoundingClientRect();
      const leftEdge = viewport?.offsetLeft || 0, topEdge = viewport?.offsetTop || 0;
      const rightEdge = leftEdge + (viewport?.width || innerWidth), bottomEdge = topEdge + (viewport?.height || innerHeight);
      const left = Math.max(leftEdge + 16, Math.min(rect.left, rightEdge - panel.width - 16));
      const preferredTop = rect.bottom + panel.height + 24 <= bottomEdge ? rect.bottom + 8 : rect.top - panel.height - 8;
      const top = Math.max(topEdge + 16, Math.min(preferredTop, bottomEdge - panel.height - 16));
      this.panel.style.setProperty('--ges-price-left', `${left}px`);
      this.panel.style.setProperty('--ges-price-top', `${top}px`);
    }
    syncInputs() {
      const params = new URLSearchParams(location.search);
      const keys = params.has(contextKeys[0]) ? marketKeys : nativeKeys;
      this.min.value = params.get(keys[0]) || ''; this.max.value = params.get(keys[1]) || '';
      const active = keys.some(key => params.has(key) && params.get(key) !== '');
      this.results.querySelectorAll('[data-ges-price-badge]').forEach(badge => { badge.hidden = !active; });
    }
    apply() {
      this.max.setCustomValidity(''); this.min.setCustomValidity('');
      const min = amount(this.min.value), max = amount(this.max.value);
      if ((min !== null && (!Number.isFinite(min) || min < 0)) || (max !== null && (!Number.isFinite(max) || max < 0))) {
        this.min.setCustomValidity(words('Ingresa un precio válido.', 'Enter a valid price.')); this.form.reportValidity(); return;
      }
      if (min !== null && max !== null && max < min) {
        this.max.setCustomValidity(words('El máximo debe ser igual o mayor que el mínimo.', 'The maximum must be greater than or equal to the minimum.')); this.form.reportValidity(); return;
      }
      if (!this.form.reportValidity()) return;
      const url = new URL(location.href); allKeys.forEach(key => url.searchParams.delete(key)); url.searchParams.delete('page');
      const native = this.dataset.native === 'true', keys = native ? nativeKeys : marketKeys;
      if (min !== null) url.searchParams.set(keys[0], String(min));
      if (max !== null) url.searchParams.set(keys[1], String(max));
      if (!native && (min !== null || max !== null)) {
        url.searchParams.set(contextKeys[0], this.dataset.currency); url.searchParams.set(contextKeys[1], this.dataset.country);
      }
      if (native && (min !== null || max !== null)) {
        url.searchParams.set(nativeContext[0], this.dataset.currency); url.searchParams.set(nativeContext[1], this.dataset.country);
      }
      location.assign(url);
    }
    async scan() {
      this.controller?.abort(); this.controller = new AbortController();
      const signal = this.controller.signal, results = this.results;
      results.setAttribute('data-ges-price-active', ''); results.setAttribute('data-ges-price-loading', '');
      results.infinityScrollObserver?.disconnect();
      const params = new URLSearchParams(location.search);
      const min = amount(params.get(marketKeys[0])), max = amount(params.get(marketKeys[1]));
      const setStatus = value => { this.status.textContent = value; };
      setStatus(words('Buscando en toda la colección…', 'Searching the full collection…'));
      const pages = new Map(); let expectedPages = 1, expectedTotal = 0, perPage = 24;
      const fetchPage = async page => {
        const url = new URL(location.href);
        allKeys.forEach(key => url.searchParams.delete(key));
        url.searchParams.set('section_id', this.dataset.section); url.searchParams.set('page', String(page));
        const timeout = setTimeout(() => this.controller.abort(), 30000);
        let response, html;
        try {
          response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal });
          if (!response.ok) throw new Error('Page unavailable'); html = await response.text();
        } finally { clearTimeout(timeout); }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const meta = doc.querySelector('[data-ges-price-page]');
        if (!meta || Number(meta.dataset.page) !== page || meta.dataset.currency !== this.dataset.currency || meta.dataset.country !== this.dataset.country) throw new Error('Unexpected context');
        if (page === 1) {
          expectedPages = Math.max(1, Number(meta.dataset.pages)); expectedTotal = Number(meta.dataset.total); perPage = Number(meta.dataset.size) || 24;
          if (!Number.isFinite(expectedTotal) || expectedTotal > 25000 || expectedPages > 3125) throw new Error('Pagination limit');
        } else if (Number(meta.dataset.total) !== expectedTotal || Math.max(1, Number(meta.dataset.pages)) !== expectedPages) throw new Error('Collection changed');
        const cards = [...doc.querySelectorAll('.product-grid > li[data-product-id][data-ges-price]')];
        // Empty search pages render recommendations in the original theme; these are not results.
        pages.set(page, expectedTotal === 0 ? [] : cards);
        setStatus(words(`Revisando página ${pages.size} de ${expectedPages}…`, `Checking page ${pages.size} of ${expectedPages}…`));
        return doc;
      };
      try {
        if ((min !== null && (!Number.isFinite(min) || min < 0)) || (max !== null && (!Number.isFinite(max) || max < 0)) || (min !== null && max !== null && min > max)) throw new Error('Invalid range');
        const first = await fetchPage(1);
        let next = 2;
        await Promise.all(Array.from({ length: Math.min(3, expectedPages - 1) }, async () => {
          while (next <= expectedPages && !signal.aborted) await fetchPage(next++);
        }));
        if (signal.aborted || !this.isConnected) return;
        const seen = new Set(), cards = [];
        for (let page = 1; page <= expectedPages; page++) {
          for (const card of pages.get(page) || []) {
            if (seen.has(card.dataset.productId)) throw new Error('Collection changed');
            seen.add(card.dataset.productId);
            const price = Number(card.dataset.gesPrice);
            if (!Number.isFinite(price)) throw new Error('Missing price');
            if ((min === null || price >= Math.round(min * 100)) && (max === null || price <= Math.round(max * 100))) cards.push(card);
          }
        }
        // For search, Shopify's total may also include pages/articles. All result pages were still scanned.
        if (!location.pathname.endsWith('/search') && seen.size !== expectedTotal) throw new Error('Incomplete collection');
        const sourceGrid = first.querySelector('.product-grid'), target = results.querySelector('#ResultsList');
        if (!target) throw new Error('Missing results');
        let grid = results.querySelector('.product-grid');
        if (!grid && sourceGrid) { grid = sourceGrid.cloneNode(false); target.replaceChildren(grid); }
        results.querySelectorAll('.pagination, [ref="viewMorePrevious"], [ref="viewMoreNext"]').forEach(el => el.remove());
        results.querySelectorAll('.main-collection-grid__empty').forEach(el => el.remove());
        if (grid) grid.dataset.lastPage = '1';
        this.cards = cards; this.pageSize = perPage; this.grid = grid;
        const nav = document.createElement('nav'); nav.className = 'ges-price-pagination'; nav.setAttribute('aria-label', words('Páginas de resultados', 'Result pages')); target.append(nav); this.nav = nav;
        this.draw(1);
        setStatus(words(`${cards.length} productos en este rango.`, `${cards.length} products in this range.`));
        results.querySelectorAll('[data-testid="products-count"] [role="status"]').forEach(el => { el.textContent = words(`${cards.length} productos`, `${cards.length} products`); });
        if (!cards.length) {
          const empty = document.createElement('p'); empty.className = 'main-collection-grid__empty';
          empty.textContent = words('No hay productos en este rango. Prueba otro precio o limpia el filtro.', 'No products in this range. Try another price or clear the filter.'); target.append(empty);
        }
        results.removeAttribute('data-ges-price-loading');
      } catch (error) {
        this.controller.abort();
        if (!this.isConnected) return;
        // Do not present partial/unfiltered cards as a completed filtered result.
        const target = results.querySelector('#ResultsList'); if (target) target.hidden = true;
        results.removeAttribute('data-ges-price-loading');
        setStatus(error.message === 'Pagination limit' ? words('Esta colección es demasiado grande para completar el rango. Usa una categoría o un filtro adicional.', 'This collection is too large to complete the price range. Choose a category or an additional filter.') : words('No pudimos completar el filtro. Reintenta con Aplicar o usa Limpiar para volver a todos los productos.', 'We could not complete the filter. Retry with Apply or use Clear to see all products.'));
      }
    }
    draw(page) {
      const count = Math.max(1, Math.ceil(this.cards.length / this.pageSize));
      this.grid?.replaceChildren(...this.cards.slice((page - 1) * this.pageSize, page * this.pageSize));
      this.nav.replaceChildren();
      if (count < 2) return;
      const prev = document.createElement('button'), next = document.createElement('button'), status = document.createElement('span');
      prev.type = next.type = 'button'; prev.className = next.className = 'button';
      prev.textContent = words('Anterior', 'Previous'); next.textContent = words('Siguiente', 'Next');
      prev.disabled = page === 1; next.disabled = page === count; status.textContent = `${page} / ${count}`;
      const move = target => { this.draw(target); this.grid?.scrollIntoView({ block: 'start', behavior: 'smooth' }); this.nav.querySelector('button:not(:disabled)')?.focus({ preventScroll: true }); };
      prev.addEventListener('click', () => move(page - 1)); next.addEventListener('click', () => move(page + 1));
      this.nav.append(prev, status, next);
    }
  }
  customElements.define('ges-price-filter', GESPriceFilter);
  document.addEventListener('filter:update', () => document.querySelectorAll('ges-price-filter').forEach(el => el.syncInputs()));
})();
